import CryptoJS from 'crypto-js';

/**
 * salaryCrypto.ts
 * ----------------
 * Client-side decryption of sensitive salary-config values. The backend stores
 * and returns these fields encrypted (see ibook-backend src/utils/fieldCrypto.js);
 * the UI decrypts them just before display.
 *
 * Scheme (must match backend exactly):
 *   key = SHA-256(REACT_APP_SALARY_ENCRYPTION_KEY)   (32 bytes)
 *   AES-256-CBC, random 16-byte IV per value
 *   ciphertext string: enc:v1:<iv_hex>:<cipher_hex>
 */

const PREFIX = 'enc:v1:';

// SHA-256 of the shared secret → 256-bit key (same derivation as the backend).
const getKey = (): CryptoJS.lib.WordArray | null => {
  const secret = process.env.REACT_APP_SALARY_ENCRYPTION_KEY;
  if (!secret) return null;
  return CryptoJS.SHA256(secret);
};

/** True if the value is one of our encrypted ciphertext strings. */
export const isEncrypted = (value: any): boolean =>
  typeof value === 'string' && value.startsWith(PREFIX);

/**
 * Decrypt a single value. Non-ciphertext (plaintext number/string, Decimal128
 * object, null) is returned as-is, so it's safe to call on any field.
 * Returns the decrypted plaintext string (or the original value if not encrypted).
 */
export const decryptValue = (value: any): any => {
  if (!isEncrypted(value)) return value;
  const key = getKey();
  if (!key) {
    console.warn('[salaryCrypto] REACT_APP_SALARY_ENCRYPTION_KEY not set — cannot decrypt');
    return value;
  }
  try {
    const body = (value as string).slice(PREFIX.length);
    const [ivHex, dataHex] = body.split(':');
    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Hex.parse(dataHex),
    });
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    console.warn('[salaryCrypto] decrypt failed', e);
    return value;
  }
};

/** Decrypt and coerce to a Number (0 when empty/invalid). */
export const decryptNumber = (value: any): number => {
  const plain = decryptValue(value);
  if (plain === null || plain === undefined || plain === '') return 0;
  // Decimal128 comes through as { $numberDecimal: '...' } — handle that too.
  if (typeof plain === 'object' && plain.$numberDecimal !== undefined) {
    return Number(plain.$numberDecimal) || 0;
  }
  const n = Number(plain);
  return Number.isFinite(n) ? n : 0;
};

const HEAD_ARRAYS = ['companyAllowanceHeads', 'employerContributionHeads', 'deductionsHeads'];

/**
 * Decrypt a whole EmployeeSalaryConfig object for display: ctcAmount + each
 * head's `value`. Returns a new object; the original is left untouched.
 */
export const decryptSalaryConfig = (config: any): any => {
  if (!config || typeof config !== 'object') return config;
  const out = { ...config };
  if (out.ctcAmount !== undefined && out.ctcAmount !== null) {
    out.ctcAmount = decryptValue(
      typeof out.ctcAmount === 'object' && out.ctcAmount.$numberDecimal !== undefined
        ? out.ctcAmount.$numberDecimal
        : out.ctcAmount
    );
  }
  HEAD_ARRAYS.forEach((key) => {
    if (Array.isArray(out[key])) {
      out[key] = out[key].map((h: any) =>
        h && typeof h === 'object'
          ? { ...h, value: h.value !== undefined ? decryptValue(h.value) : h.value }
          : h
      );
    }
  });
  return out;
};

/**
 * Decrypt the sensitive MONETARY fields of a SalaryPayment (Process Salary
 * record) for display. Mirrors the backend salaryPaymentCrypto helper: only
 * rupee amounts are decrypted; percentages (taxConfig rate), day counts, labels
 * and ids are left as-is. Numbers come back as real Numbers so downstream
 * parseFloat()/formatting keep working. Returns a NEW object; original untouched.
 */
export const decryptSalaryPayment = (payment: any): any => {
  if (!payment || typeof payment !== 'object') return payment;
  const out = { ...payment };

  // ---- earnings ----
  if (out.earnings && typeof out.earnings === 'object') {
    const e = { ...out.earnings };
    if (e.basic !== undefined) e.basic = decryptNumber(e.basic);
    if (e.hra !== undefined) e.hra = decryptNumber(e.hra);
    if (e.totalEarnings !== undefined) e.totalEarnings = decryptNumber(e.totalEarnings);
    if (Array.isArray(e.additionalEarnings)) {
      e.additionalEarnings = e.additionalEarnings.map((r: any) =>
        r && typeof r === 'object' && r.amount !== undefined ? { ...r, amount: decryptNumber(r.amount) } : r);
    }
    if (Array.isArray(e.allowances)) {
      e.allowances = e.allowances.map((r: any) =>
        r && typeof r === 'object' && r.amount !== undefined ? { ...r, amount: decryptNumber(r.amount) } : r);
    }
    out.earnings = e;
  }

  // ---- deductions ----
  if (out.deductions && typeof out.deductions === 'object') {
    const d = { ...out.deductions };
    ['pf', 'esi', 'tds', 'professionalTax', 'lwf', 'advance', 'loan', 'totalDeductions'].forEach((k) => {
      if (d[k] !== undefined) d[k] = decryptNumber(d[k]);
    });
    if (Array.isArray(d.otherDeductions)) {
      d.otherDeductions = d.otherDeductions.map((r: any) =>
        r && typeof r === 'object' && r.amount !== undefined ? { ...r, amount: decryptNumber(r.amount) } : r);
    }
    out.deductions = d;
  }

  // ---- employer contributions ----
  if (out.employerContributions && typeof out.employerContributions === 'object') {
    const c = { ...out.employerContributions };
    ['pf', 'esi', 'gratuity', 'totalContributions'].forEach((k) => {
      if (c[k] !== undefined) c[k] = decryptNumber(c[k]);
    });
    out.employerContributions = c;
  }

  // ---- taxConfig: only the money amounts, NOT the rate (%) ----
  if (out.taxConfig && typeof out.taxConfig === 'object' && out.taxConfig.tdsConfig
      && typeof out.taxConfig.tdsConfig === 'object') {
    const t = { ...out.taxConfig.tdsConfig };
    if (t.taxableAmount !== undefined) t.taxableAmount = decryptNumber(t.taxableAmount);
    if (t.amount !== undefined) t.amount = decryptNumber(t.amount);
    out.taxConfig = { ...out.taxConfig, tdsConfig: t };
  }

  // ---- top-level totals ----
  ['grossSalary', 'netSalary', 'ctc', 'payableAmount', 'lopAmount'].forEach((k) => {
    if (out[k] !== undefined) out[k] = decryptNumber(out[k]);
  });

  return out;
};

/** Decrypt a SalaryGrade object's gradeValue for display. Returns a new object. */
export const decryptSalaryGrade = (grade: any): any => {
  if (!grade || typeof grade !== 'object') return grade;
  const out = { ...grade };
  if (out.gradeValue !== undefined && out.gradeValue !== null) {
    out.gradeValue = decryptValue(
      typeof out.gradeValue === 'object' && out.gradeValue.$numberDecimal !== undefined
        ? out.gradeValue.$numberDecimal
        : out.gradeValue
    );
  }
  return out;
};
